import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetCampaignFiltersMutation } from "../../../redux/services/campaignApi";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import {
  selectCampaignCommunicationsFilters,
  setCampaignCommunicationsCampaignStatus,
  FilterState,
} from "../../../redux/slices/filterSlice";

interface CampaignCommStatusFilterProps {
  onClose: () => void;
  communicationMode: { value: string; displayValue: string };
  onApply?: (selectedStatuses: string[]) => void;
}

const CampaignCommStatusFilter = ({
  onClose,
  communicationMode,
}: CampaignCommStatusFilterProps) => {
  const selectedStatus = useSelector(
    (state: { filter: FilterState }) =>
      selectCampaignCommunicationsFilters(state)?.status
  );

  const [campaignFilters, setCampaignFilters] = useState<
    Array<{ name: string; category_dropdown: string }>
  >([]);

  // Temporary state for selections before applying
  const [tempSelectedStatuses, setTempSelectedStatuses] = useState<Set<string>>(
    new Set(selectedStatus.value)
  );

  const [getCampaignFilters, { isLoading }] = useGetCampaignFiltersMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchStatuses = async (channel: string) => {
      try {
        const response = await getCampaignFilters({
          channel: channel,
        }).unwrap();
        if (response) {
          setCampaignFilters(response);
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
        setCampaignFilters([]);
      }
    };

    if (communicationMode?.value) {
      fetchStatuses(communicationMode.value);
    }
  }, [communicationMode, getCampaignFilters]);

  const handleToggleStatus = (status: string) => {
    setTempSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleApply = () => {
    // Get the corresponding filter names for selected category_dropdowns
    const selectedValues = Array.from(tempSelectedStatuses);
    const selectedNames = selectedValues.map((value) => {
      const filter = campaignFilters.find((f) => f.category_dropdown === value);
      return filter?.name || value;
    });
    console.log(selectedNames, "selectedNames");
    dispatch(
      setCampaignCommunicationsCampaignStatus({
        value: selectedValues,
        displayValue: selectedNames.join(", "),
      })
    );

    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Selected Statuses Display */}
      {selectedStatus.value.length > 0 && (
        <div className="flex flex-col">
          {(selectedStatus.value as string[]).map((statusValue: string) => {
            const statusObj = campaignFilters.find(
              (filter) => filter.category_dropdown === statusValue
            );
            const displayName = statusObj?.name || statusValue;
            return (
              <div
                key={statusValue}
                className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {displayName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`shrink-0 w-[14px] h-[14px] border border-gray-100 dark:border-gray-100-dk rounded-[3px] flex items-center justify-center ml-2 ${
                      tempSelectedStatuses.has(statusValue)
                        ? "bg-verba-blue dark:bg-verba-blue-dk"
                        : ""
                    }`}
                    onClick={() => handleToggleStatus(statusValue)}
                  >
                    {tempSelectedStatuses.has(statusValue) && (
                      <CheckIcon className="w-3 h-3 text-white dark:text-white-dk" />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Status List */}
      <div className="flex flex-col">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Status
        </div>

        <div className="overflow-y-auto">
          {campaignFilters.length === 0 || isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading statuses..." : "No statuses available"}
              </div>
            </div>
          ) : (
            campaignFilters
              .filter(
                (filter) =>
                  !selectedStatus.value.includes(filter.category_dropdown)
              )
              .map((filter) => (
                <div
                  key={filter.category_dropdown}
                  onClick={() => handleToggleStatus(filter.category_dropdown)}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {filter.name}
                    </div>
                  </div>
                  <div
                    className={`shrink-0 w-[14px] h-[14px] border border-gray-100 dark:border-gray-100-dk rounded-[3px] flex items-center justify-center ml-2 ${
                      tempSelectedStatuses.has(filter.category_dropdown)
                        ? "bg-verba-blue dark:bg-verba-blue-dk"
                        : ""
                    }`}
                  >
                    {tempSelectedStatuses.has(filter.category_dropdown) && (
                      <CheckIcon className="w-3 h-3 text-white dark:text-white-dk" />
                    )}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignCommStatusFilter;
